import { useState } from 'react';
import { useRouter } from '@/lib/navigation';
import { useTranslations } from '@/i18n';
import { format, parseISO, differenceInDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useDeleteCity, DeleteCityError } from '@/hooks/trips/useDeleteCity';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog';
import SavedExpenseCard from '@/components/trips/cards/SavedExpenseCard';
import type { TripDetailCity } from '@/hooks/trips/types';

interface CitiesListProps {
  tripId: string;
  cities?: TripDetailCity[];
  canManageCities?: boolean;
  /** Set of city IDs that have date validation issues */
  invalidCityIds?: Set<number>;
}

interface CityItemProps {
  tripId: string;
  city: TripDetailCity;
  onDelete?: () => void;
  canManageCities: boolean;
  /** Whether this city has date validation issues */
  hasDateIssue?: boolean;
}

function CityItem({
  tripId,
  city,
  onDelete,
  canManageCities,
  hasDateIssue = false,
}: CityItemProps) {
  const router = useRouter();
  const tExpenses = useTranslations('expenses');
  const tTrips = useTranslations('trips');
  const tDeleteCityDialog = useTranslations('trips.deleteCityDialog');
  const deleteCityMutation = useDeleteCity(tripId);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const formattedArrival = city.arrivalDate
    ? format(parseISO(city.arrivalDate), 'dd/MM/yyyy')
    : '-';
  const formattedDeparture = city.departureDate
    ? format(parseISO(city.departureDate), 'dd/MM/yyyy')
    : '-';
  const duration =
    city.arrivalDate && city.departureDate
      ? differenceInDays(parseISO(city.departureDate), parseISO(city.arrivalDate)) + 1
      : 0;
  const totalAmount = Number(city.totalAmount ?? 0);

  const hotels = city.hotels ?? [];
  const transportations = city.transportations ?? [];
  const fuels = city.fuels ?? [];
  const hasExpenses = hotels.length > 0 || transportations.length > 0 || fuels.length > 0;

  const handleEdit = () => {
    if (!canManageCities) {
      return;
    }
    router.push(`/trips/${tripId}/cities/${city.id}`);
  };

  const handleDelete = () => {
    if (!canManageCities) {
      return;
    }
    deleteCityMutation.reset();
    setDeleteError(null);
    setShowDeleteConfirm(true);
  };

  const getDeleteErrorMessage = (error: unknown) => {
    if (error instanceof DeleteCityError && error.status === 403) {
      return tTrips('deleteCityError.forbidden');
    }
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return tTrips('deleteCityError.generic');
  };

  const handleDialogClose = () => {
    if (deleteCityMutation.isPending) {
      return;
    }
    setShowDeleteConfirm(false);
    setDeleteError(null);
    deleteCityMutation.reset();
  };

  const confirmDelete = async () => {
    setDeleteError(null);

    try {
      await deleteCityMutation.mutateAsync(city.id);
      handleDialogClose();
      onDelete?.();
    } catch (error) {
      setDeleteError(getDeleteErrorMessage(error));
    }
  };

  const toggleDetails = () => {
    setIsExpanded((prev) => !prev);
  };

  const formatAmount = (value: number | null | undefined) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return '0';
    }

    if (Number.isInteger(value)) {
      return value.toString();
    }

    return value.toFixed(2);
  };

  return (
    <div
      className={cn(
        'box-border flex w-full flex-col gap-5 rounded-2xl p-5 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]',
        hasDateIssue ? 'border border-[#e5484d] bg-[#fff5f5]' : 'bg-white'
      )}
    >
      <div className="flex w-full flex-col gap-2.5">
        <div className="flex w-full flex-col gap-4">
          <div className="flex w-full min-w-0 items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 flex-col justify-center">
              <h3 className="break-words text-2xl leading-8 font-medium text-[#292929]">
                {city.cityName}, {city.countryName}
              </h3>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {canManageCities && (
                <Button
                  onClick={handleEdit}
                  variant="unstyled"
                  size="none"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-white p-2 transition-colors hover:bg-[#f9f9f9]"
                >
                  <img src="/assets/icons/edit.svg" alt="" className="h-5 w-5" />
                </Button>
              )}
              {canManageCities && (
                <Button
                  onClick={handleDelete}
                  variant="unstyled"
                  size="none"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-white p-2 transition-colors hover:bg-[#f9f9f9] disabled:opacity-60"
                  disabled={deleteCityMutation.isPending}
                >
                  <img src="/assets/icons/delete.svg" alt="" className="h-5 w-5" />
                </Button>
              )}
            </div>
          </div>

          <div className="flex w-full flex-col gap-1">
            <div className="flex items-center gap-2">
              <img src="/assets/icons/calendar.svg" alt="" className="h-[18px] w-[18px]" />
              <span
                className={cn(
                  'text-[15px] leading-6',
                  hasDateIssue ? 'text-[#e5484d]' : 'text-[#292929]'
                )}
              >
                {formattedArrival} - {formattedDeparture} • {duration} {tExpenses('days')}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <img src="/assets/icons/money-paper-total.svg" alt="" className="h-[18px] w-[18px]" />
              <span className="text-[15px] leading-6 font-medium text-[#18794e]">
                {formatAmount(totalAmount)} GEL
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <Button
          onClick={toggleDetails}
          variant="unstyled"
          size="none"
          aria-expanded={isExpanded}
          className="flex justify-start gap-3 text-[0.875rem] text-[#292929]"
        >
          <span>
            {isExpanded ? tExpenses('hideExpenseDetails') : tExpenses('showExpenseDetails')}
          </span>
          <img
            src="/assets/icons/chevron-down.svg"
            alt=""
            className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </Button>

        {isExpanded && (
          <div className="flex flex-col gap-4">
            {hasExpenses ? (
              <>
                {hotels.map((hotel) => (
                  <SavedExpenseCard
                    key={`hotel-${hotel.id}`}
                    icon="/assets/icons/building.svg"
                    title={tExpenses('accommodation')}
                    dateRange={
                      hotel.checkInDate && hotel.checkOutDate
                        ? {
                            checkInDate: parseISO(hotel.checkInDate),
                            checkOutDate: parseISO(hotel.checkOutDate),
                          }
                        : undefined
                    }
                    amount={formatAmount(hotel.totalAmount)}
                    currency={hotel.prepaymentCurrencyName || '₾'}
                    showActions={false}
                  />
                ))}

                {transportations.map((transportation) => {
                  const assistanceLabel =
                    transportation.assistanceType === 'rental_car'
                      ? tExpenses('transportOptions.rentalCar')
                      : transportation.assistanceType === 'car_with_driver'
                        ? tExpenses('transportOptions.carWithDriver')
                        : tExpenses('transportOptions.ticketOrAdvance');
                  const hasAmount =
                    typeof transportation.amount === 'number' && transportation.amount > 0;

                  return (
                    <SavedExpenseCard
                      key={`transport-${transportation.id}`}
                      icon="/assets/icons/plane.svg"
                      title={assistanceLabel}
                      subtitle={transportation.companyName || tExpenses('transport')}
                      amount={hasAmount ? formatAmount(transportation.amount) : ''}
                      currency={
                        hasAmount ? transportation.prepaymentCurrencyName || '₾' : undefined
                      }
                      formattedAmount={hasAmount ? undefined : tExpenses('transportNoAmount')}
                      showActions={false}
                    />
                  );
                })}

                {fuels.map((fuel) => (
                  <SavedExpenseCard
                    key={`fuel-${fuel.id}`}
                    icon="/assets/icons/gas-station.svg"
                    title={fuel.fuelTypeName || tExpenses('fuel')}
                    subtitle={fuel.carModel}
                    amount={formatAmount(fuel.fuelAmount)}
                    formattedAmount={`${formatAmount(fuel.fuelAmount)} ${tExpenses('liters')}`}
                    showActions={false}
                  />
                ))}
              </>
            ) : (
              <div className="rounded-2xl border border-[#f0f0f0] bg-[#f9f9f9] px-4 py-3 text-sm leading-5 text-[#666666]">
                {tExpenses('noExpenses')}
              </div>
            )}
          </div>
        )}
      </div>

      {canManageCities && (
        <ConfirmationDialog
          isOpen={showDeleteConfirm}
          onClose={handleDialogClose}
          onConfirm={confirmDelete}
          title={tDeleteCityDialog('title')}
          message={tDeleteCityDialog('message')}
          confirmText={tDeleteCityDialog('confirm')}
          cancelText={tDeleteCityDialog('cancel')}
          confirmButtonVariant="danger"
          isLoading={deleteCityMutation.isPending}
          errorMessage={deleteError ?? undefined}
        />
      )}
    </div>
  );
}

export default function CitiesList({
  tripId,
  cities = [],
  canManageCities = true,
  invalidCityIds,
}: CitiesListProps) {
  const [refreshToken, setRefreshToken] = useState(0);

  const handleCityDeleted = () => {
    setRefreshToken((token) => token + 1);
  };

  if (!cities.length) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4" data-refresh-token={refreshToken}>
      {cities.map((city) => (
        <CityItem
          key={city.id}
          tripId={tripId}
          city={city}
          onDelete={handleCityDeleted}
          canManageCities={canManageCities}
          hasDateIssue={invalidCityIds?.has(city.id) ?? false}
        />
      ))}
    </div>
  );
}
